/**
 * Google Login Button Component
 * Created: 2024-04-22
 * Updated: 2024-12-17 - CRITICAL FIX: Direct oauth-api integration to resolve Google OAuth failures * Updated: 2025-01-18 - FIXED: Use runtime config instead of process.env to support environment-specific URLs
 * Updated: 2025-01-21 - FIXED: Removed unused clientImports import that was causing build failures
 *
 * Provides a button for Google OAuth authentication with direct oauth-api integration
 */

'use client'

import React, {useCallback} from 'react'
import {useSearchParams} from 'next/navigation'
import {useApiUrl} from '../../contexts/api-url.context'
import qs from 'qs'
import {Button, HStack, Image, Text} from '@chakra-ui/react'

interface GoogleLoginButtonProps {
  isDisabled?: boolean
}

export const GoogleLoginButton = ({isDisabled = false}: GoogleLoginButtonProps) => {
  const searchParams = useSearchParams()
  const {apiUrl} = useApiUrl()

  // Get OAuth parameters to pass through to Google OAuth flow
  const clientId = searchParams.get('client_id')
  const redirectUri = searchParams.get('redirect_uri')
  const responseType = searchParams.get('response_type')
  const scope = searchParams.get('scope')

  const handleGoogleLogin = useCallback(() => {
    const oauthApiUrl = apiUrl || 'http://localhost:8081'

    // Build the state parameter to include OAuth parameters
    const state = qs.stringify({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: responseType,
      scope
    })

    // CRITICAL FIX: Point directly to oauth-api instead of proxy
    // This eliminates the double redirect and state parameter issues
    const googleAuthUrl = `${oauthApiUrl}/auth/google?${qs.stringify({state})}`

    console.log('State parameter being sent:', state)
    console.log('Full Google OAuth URL:', googleAuthUrl)
    console.log('Google OAuth: Redirecting directly to oauth-api:', googleAuthUrl)
    console.log('Runtime OAuth API URL (from useApiUrl hook):', oauthApiUrl)

    // Redirect to Google login via oauth-api directly
    window.location.href = googleAuthUrl
  }, [clientId, redirectUri, responseType, scope, apiUrl])

  return (
    <Button
      onClick={handleGoogleLogin}
      isDisabled={isDisabled}
      width="full"
      variant="outline"
      colorScheme="gray"
      borderColor="gray.300"
      height="42px"
      _hover={{bg: 'gray.50'}}
      _active={{bg: 'gray.100'}}>
      <HStack spacing={2} width="full" justifyContent="center">
        <Image
          src="/google-logo.svg"
          alt="Google logo"
          boxSize="18px"
          fallback={
            <Text fontSize="lg" fontWeight="bold">
              G
            </Text>
          }
        />
        <Text>Sign in with Google</Text>
      </HStack>
    </Button>
  )
}
